<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import {
  type FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";

import {
  BooleanDisplayFormat,
  type BooleanFormElement,
  type FormElementType,
} from "~/api";
import { emptyGuid } from "~/utils/constants";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
const props = defineProps<DefaultFormComponentProps<BooleanFormElement>>();

const formState = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const { useAnswer } = formState;
const answer = useAnswer<FormElementType.BOOLEAN>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);
const localAnswer = ref(answer.value);

watch(
  () => answer.value,
  (newVal) => {
    localAnswer.value = newVal;
  },
);

const inPdf = useIsInPdfMode();

/** When SaveAnsweringUserGuid is true, display who checked the box */
const answeringUserName = computed(() => {
  if (!elementProperties.value.saveAnsweringUserGuid || !localAnswer.value)
    return null;
  const key =
    props.modelValue.questionCode +
    "-" +
    (props.modelValue.repeaterGuid ?? emptyGuid);
  return (formState.answers[key] as any)?.answeringUserName as
    | string
    | null
    | undefined;
});

const deselectAnswer = () => {
  localAnswer.value = undefined;
  answer.value = undefined;
};
</script>

<template id="form-boolean-component">
  <div class="BooleanComponent">
    <slot name="label" :element="modelValue" />
    <div class="BooleanComponentAnswer">
      <el-checkbox
        v-if="elementProperties.displayFormat === BooleanDisplayFormat.CHECKBOX"
        v-model="localAnswer"
        :disabled="isReadOnly"
        @update:model-value="answer = $event as boolean | undefined"
      >
        <span v-if="answeringUserName" class="BooleanAnsweringUser">
          {{ answeringUserName }}
        </span>
      </el-checkbox>
      <el-radio-group
        v-if="elementProperties.displayFormat === BooleanDisplayFormat.YES_NO"
        v-model="localAnswer"
        :disabled="isReadOnly"
        @update:model-value="answer = $event as boolean | undefined"
      >
        <el-radio-button
          v-if="!inPdf || localAnswer || localAnswer === undefined"
          :value="true"
          >Yes</el-radio-button
        >
        <el-radio-button
          v-if="!inPdf || !localAnswer || localAnswer === undefined"
          :value="false"
          >No</el-radio-button
        >
      </el-radio-group>

      <el-text
        v-if="
          !isReadOnly &&
          !elementProperties.noDeselect &&
          localAnswer !== undefined &&
          elementProperties.displayFormat === BooleanDisplayFormat.YES_NO
        "
        tag="ins"
        class="BooleanComponentDeselect"
        @click="deselectAnswer"
        >Deselect</el-text
      >
    </div>
  </div>
</template>

<style lang="less">
.BooleanComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  .BooleanComponentAnswer {
    display: flex;
    flex-direction: column;

    .BooleanAnsweringUser {
      font-size: 0.85em;
      color: var(--el-text-color-secondary);
    }

    .BooleanComponentDeselect {
      cursor: pointer;
      place-self: flex-end;
      margin-top: 2px;
      text-align: left;
      font-size: 0.8em;
    }
  }

  .el-radio-group {
    flex-wrap: nowrap;
    margin: 0;
    .el-radio-button__original-radio:disabled:checked
      + .el-radio-button__inner {
      background-color: #f2f2f2;
      color: #444142;
      font-weight: normal;
    }
    .el-radio-button__original-radio:disabled + .el-radio-button__inner {
      background-color: var(--el-color-primary-light-9);
      color: #cfcfcf;
      font-weight: normal;
    }
  }
}

body.forPdf .BooleanComponent {
  .el-checkbox__input.is-checked .el-checkbox__inner:after {
    border-color: black;
  }
  .el-checkbox__input .el-checkbox__inner {
    border-width: 1px;
  }

  .el-radio-button.is-disabled {
    opacity: 1;
  }

  .el-radio-button__original-radio:disabled + .el-radio-button__inner,
  .el-radio-button__original-radio:disabled:checked + .el-radio-button__inner {
    color: black;
    border-color: #444142;
    background-color: #f2f2f2;
    box-shadow: -1px 0 0 0 #444142;
  }
}
</style>
